import {useCallback} from "react";
import {EBirdMyDataSchema} from "ebird-mydata-reader";
import type {EBirdMyDataSchemaWithImage} from "../views/discover/big-year/types";
import type {SpeciesMetadataEntry} from "./speciesMetadataLookup";

const FALLBACK_BIRD_IMAGE = '/assets/fallback-bird.svg';

export function useObservationImages(
    speciesImages: any,
    lookupByTaxonomicOrder: (taxonomicOrder: number) => SpeciesMetadataEntry|undefined,
    getImageTaxonomicOrder: (taxonomicOrder: number) => number = taxonomicOrder => taxonomicOrder
) {
    const getSpeciesImage = useCallback((taxonomicOrder: number, size: 'thumb'|'medium') => {
        if (Object.prototype.hasOwnProperty.call(speciesImages, taxonomicOrder)) {
            return speciesImages[taxonomicOrder]?.[size];
        }

        const fallbackTaxonomicOrder = getImageTaxonomicOrder(taxonomicOrder);
        if (fallbackTaxonomicOrder !== taxonomicOrder && Object.prototype.hasOwnProperty.call(speciesImages, fallbackTaxonomicOrder)) {
            return speciesImages[fallbackTaxonomicOrder]?.[size];
        }

        return undefined;
    }, [getImageTaxonomicOrder, speciesImages]);

    const setObservationImages = useCallback((observations: EBirdMyDataSchema[]): EBirdMyDataSchemaWithImage[] => {
        for (let observation of observations as EBirdMyDataSchemaWithImage[]) {
            observation.image = getSpeciesImage(observation.taxonomicOrder, 'medium') ?? FALLBACK_BIRD_IMAGE;

            const taxonomicData = lookupByTaxonomicOrder(observation.taxonomicOrder);
            if (taxonomicData) {
                observation.speciesGroup = taxonomicData.speciesGroup;
            }
        }

        return observations as EBirdMyDataSchemaWithImage[];
    }, [getSpeciesImage, lookupByTaxonomicOrder]);

    const setObservationImagesForMarkers = useCallback((markers: any) => {
        for (let marker of markers.features) {
            marker.properties.image = getSpeciesImage(marker.properties.taxonomicOrder, 'thumb') ?? FALLBACK_BIRD_IMAGE;
        }
    }, [getSpeciesImage]);

    return {
        setObservationImages,
        setObservationImagesForMarkers
    }
}
